import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, Input, Modal } from '@/components/ui'
import Loader from '@/common/components/Loader'
import FormGroup from '@/core/components/FormGroup'
import type { IUserInfo } from '@/core/services/clients'
import { useClients } from '@/home/hooks/use-clients'
import { MessageBox } from '../messages/MessageBox'
import { TroubleshootUserDetails } from './TroubleshootUserDetails'

export interface ITroubleshootValues {
  phone?: string
  email?: string
}

export interface TroubleshootUserModalProps {
  showTroubleshootModal: boolean
  setShowTroubleshootModal: (show: boolean) => void
}

const schema = yup.object().shape({
  phone: yup.string().label('Phone Number').trim().optional(),
  email: yup.string().label('Email').trim().email().optional(),
})

export const TroubleshootUserModal = (
  props: TroubleshootUserModalProps,
): JSX.Element => {
  const { showTroubleshootModal, setShowTroubleshootModal } = props

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [userInfo, setUserInfo] = useState<IUserInfo | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const { troubleshoot, loading } = useClients()

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<ITroubleshootValues>({
    mode: 'onChange',
    resolver: yupResolver<ITroubleshootValues>(schema),
    defaultValues: { phone: '', email: '' },
  })

  const handleClose = () => {
    reset()
    setUserInfo(null)
    setFormError(null)
    setShowTroubleshootModal(false)
  }

  const submit = async (values: ITroubleshootValues) => {
    setUserInfo(null)
    setFormError(null)

    // the endpoint takes one criterion at a time and prefers phone when both are sent
    const phone = values.phone?.trim()
    const email = values.email?.trim()

    if (!phone && !email) {
      setFormError('Provide either a phone number or an email')
      return
    }

    try {
      const data = await troubleshoot(phone ? { phone } : { email })
      setUserInfo(data)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  return (
    <>
      <Modal
        open={showTroubleshootModal}
        onOpenChange={(open) => (open ? undefined : handleClose())}
        size="lg"
        title={<PageTitle title="Troubleshoot User" />}
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button onClick={handleClose} variant="secondary">
              Close
            </Button>
            <Button
              form="troubleshoot-user-form"
              type="submit"
              disabled={loading}
              loading={loading}
            >
              Search
            </Button>
          </div>
        }
      >
        {loading && <Loader />}
        <form
          id="troubleshoot-user-form"
          onSubmit={handleSubmit(submit)}
          className="flex flex-col gap-4"
        >
          <FormGroup label="Phone Number" error={errors?.phone?.message}>
            <Input
              isInvalid={!!errors?.phone?.message || !!formError}
              {...register('phone')}
              type="text"
              placeholder="Enter Phone Number"
            />
          </FormGroup>
          <FormGroup label="Email" error={errors?.email?.message}>
            <Input
              isInvalid={!!errors?.email?.message || !!formError}
              {...register('email')}
              type="text"
              placeholder="Enter Email"
            />
          </FormGroup>
          <small className="text-body-small text-monochrome-2">
            Provide a phone number or an email. If both are filled in, the phone
            number is used.
          </small>
          {(formError || errors?.email?.message) && (
            <small className="text-body-small text-accent-3">
              {formError || errors?.email?.message}
            </small>
          )}
        </form>

        {userInfo && (
          <div className="mt-6">
            <TroubleshootUserDetails userInfo={userInfo} />
          </div>
        )}
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
